// eslint:disable max-func-body-length
import * as React from 'react';
import { SelectOptions, SelectOptionValue, ViewMode } from './types';
import { STATE } from 'app/constants';
import { FullscreenCodeSelector } from 'app/components';
import { compose } from 'redux';
import { routeConstants, CodeSelectorFor } from 'app/routes/route-constants';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { actionCreators } from './actions';
import { codeSelectorTranslations } from 'app/area/code-selector/constants';
import { ListViewCodeSelector } from 'app/area/list-view-code-selector/list-view-code-selector';
import { CodetableAdministrationState } from 'app/area/codetable/types-store';
import { getBreadcrumbs } from 'app/area/shared/codetable/breadcrumbs';
import { stringifyQuery } from 'app/area/shared/navigation';
import { QueryObject } from 'app/components/enhancers/with-router';
import { Tooltip } from 'ui/common/tooltip/tooltip';
import { MdOutlineStarOutline, MdStar } from 'react-icons/md';
import styles from './code-selector.scss';
import { AuthState } from 'app/types-shared';
import * as navigate from 'app/area/shared/navigation';
import { buildDraftUrl, isCaseViewModalOpen } from 'app/components/helpers';

import type { JSX } from "react";

type CodeSelectorProps = DefaultContainerProps & SelectOptions & typeof actionCreators & { breadcrumbs?: number[] };

interface CodeSelectorPropsForType {
  onOpen: () => void;
  title?: string;
  infoMessage?: string;
  urlResolver: (value: number | number[]) => string | { pathname: string; search: string };
  onClose: () => void;
}

/**
 * Support forwarding of query parameters to the selected route
 *
 * @param location
 */
const includeQueryParams = (query: QueryObject) => (route: string) => ({ pathname: route, search: stringifyQuery(query) });

const codeSelectorPropsCreator = ({
  translation,
  router,
  params,
  loadConnectedCaseTypes,
  loadCaseTypes,
  loadCompanyReports,
  loadDashboards,
  loadCodetableAdministrationList
}: CodeSelectorProps): CodeSelectorPropsForType => ({
  [CodeSelectorFor.ConnectedCaseType]: {
    onOpen: () => loadConnectedCaseTypes(router.location.query.draftId, router.location.query.connectionTypeId),
    title: translation.select_case_type,
    urlResolver: isCaseViewModalOpen() ? compose(includeQueryParams(router.location.query), routeConstants.modalViewCreateCase) : 
      compose(includeQueryParams(router.location.query), routeConstants.createCase),
    onClose: () => router.replace({
      pathname: buildDraftUrl(Number(router.location.query.draftId), 
        !!params?.favouriteId ? Number(params.favouriteId) : undefined,
        !!params?.dashboardType ? Number(params.dashboardType) : undefined,
        !!params?.bowtieCaseId ? Number(params.bowtieCaseId) : undefined),
      search: stringifyQuery({ section: router.location.query.returnToSection })
    })
  },
  [CodeSelectorFor.ChangeCaseType]: {
    onOpen: loadCaseTypes,
    title: translation.change_case_type,
    infoMessage: translation.change_case_type_info_message,
    urlResolver: isCaseViewModalOpen() ? 
      compose(includeQueryParams(router.location.query), routeConstants.modalViewChangeCaseTypeTo) :
      compose(includeQueryParams(router.location.query), routeConstants.changeCaseTypeTo),
    onClose: () => router.replace({
      pathname: buildDraftUrl(Number(router.location.query.draftId), 
        !!router.location.query.favouriteId ? Number(router.location.query.favouriteId) : undefined,
        !!router.location.query.dashboardType ? Number(router.location.query.dashboardType) : undefined,
        !!router.location.query.bowtieCaseId ? Number(router.location.query.bowtieCaseId) : undefined)
    })
  },
  [CodeSelectorFor.CaseType]: {
    onOpen: loadCaseTypes,
    urlResolver: params.type === CodeSelectorFor.CaseType ? routeConstants.createCase : 
      compose(includeQueryParams(router.location.query), routeConstants.createCase),
    onClose: () => router.replace(routeConstants.startupView)
  },
  [CodeSelectorFor.CompanyReport]: {
    onOpen: loadCompanyReports,
    title: translation.reports_company,
    urlResolver: routeConstants.companyReportFor,
    onClose: () => router.replace(routeConstants.startupView)
  },
  [CodeSelectorFor.Dashboard]: {
    onOpen: loadDashboards,
    title: translation.dashboards,
    urlResolver: routeConstants.dashboardFor,
    onClose: () => router.replace(routeConstants.startupView)
  },
  [CodeSelectorFor.CodetableAdministration]: {
    onOpen: loadCodetableAdministrationList,
    title: translation.codetable_administration,
    urlResolver: routeConstants.codetableAdministrationFor,
    onClose: () => router.replace(routeConstants.startupView)
  }
}[params.type]);

type CodeSelectorComponentType = React.FunctionComponent<React.PropsWithChildren<CodeSelectorProps>> & {
  statePath: string;
};

const shouldRenderListView = (type: keyof typeof CodeSelectorFor) => type === CodeSelectorFor.CodetableAdministration;

const CodeSelector: CodeSelectorComponentType = (props: CodeSelectorProps): JSX.Element => {
  const { items, fetching, translation, params, breadcrumbs, sharedState, checkCodetableAccess, router,
    bookmarkCaseType, removeBookmarkCaseType, draftsInfo, loadDraftsCount } = props;
  const selectorProps = codeSelectorPropsCreator(props);
  const authState = sharedState?.authState;
  const validAuthState = authState !== AuthState.Anonymous;
  const isCaseTypeSelector = params.type === CodeSelectorFor.CaseType;
  const bookmarksEnabled = isCaseTypeSelector && validAuthState;
  const textNoCodesAdded = validAuthState && isCaseTypeSelector && translation.no_active_case_types; 
  const textNoBookmarksAdded = validAuthState && isCaseTypeSelector && translation.no_bookmarked_case_types;
  const textToggleTooltip = validAuthState && isCaseTypeSelector && translation.toggle_between_case_types_and_bookmarks; 
  const selectedFolder = router?.location?.query?.folder && Number(router?.location?.query?.folder);
 
  const caseDraftInfoPanelTitle = validAuthState && isCaseTypeSelector && translation.draft_case_info_panel_title;
  const caseDraftInfoPanelContinue = validAuthState && isCaseTypeSelector && translation.draft_case_info_panel_continue;
  const caseDraftInfoPanelExpires = validAuthState && isCaseTypeSelector && translation.draft_case_info_panel_expires;
  const caseDraftInfoPanelViewAll = validAuthState && isCaseTypeSelector && translation.draft_case_info_panel_view_all;

  const [isToggledOn, setToggleStatus] = React.useState(bookmarksEnabled && props.viewModeCaseTypes === ViewMode.bookmarkedCodes);
  const options = isToggledOn ? props.bookmarkedCodes?.items : items;
  
  const routingToSpecificFolder = (): boolean => {
    
    return !!props.router?.location?.query?.folder && !!Number(!!props.router?.location?.query?.folder);
  };

  React.useEffect (()=> 
  {
    bookmarksEnabled && props.loadBookmarkedCaseTypes && props.loadBookmarkedCaseTypes();
    isCaseTypeSelector && loadDraftsCount && loadDraftsCount();
    
    if(routingToSpecificFolder()) {
      handleOnToggle(false);
    }
  }, []);

  const renderItemBookmark = (item: SelectOptionValue) => {
  
    if(!bookmarksEnabled) {
      return null;
    }
  
    const isFolder = item.descendants;
    
    const toggleBookmark = async(e: React.MouseEvent<any>) => {
      e?.preventDefault();
      e?.stopPropagation();
      if(item.bookmark) {
        removeBookmarkCaseType(item.value);
      }
      else {
        bookmarkCaseType(item.value);
      }
    };
    return(
      !isFolder && (
        item.bookmark ? 
          <div className={styles.bookMarked} onClick={toggleBookmark}>
            <Tooltip title={translation.remove_bookmark} delayed={true} center={true}>
              <MdStar/>
            </Tooltip>
          </div> : 
          <div className={styles.unbookMarked} onClick={toggleBookmark}>
            <Tooltip title={translation.bookmark_case_type} delayed={true} center={true}>
              <MdOutlineStarOutline/>
            </Tooltip>
          </div>
    ));
  };

  const handleOnToggle = async(toggledOn: boolean) => {

    if(!bookmarksEnabled) {
      return;
    }

    const {changeViewModeCaseTypes} = props;
    
    if(toggledOn) {
      await changeViewModeCaseTypes(ViewMode.bookmarkedCodes);
      navigate.toSelectCaseTypes(false);
    }  else { 
        await changeViewModeCaseTypes(ViewMode.allCodes);
    }
    setToggleStatus(toggledOn);
  };

  const overrideTitle = isToggledOn ? translation.bookmarked_case_types : translation.all_active_case_types;  
  
  if (shouldRenderListView(params.type)) {
    return <ListViewCodeSelector
      adminPanelAccess={sharedState?.access?.adminPanel}
      checkCodetableAccess={checkCodetableAccess}
      onClose={selectorProps.onClose}
      fetching={fetching}
      options={options}
      routeHandler={router.to}
      breadcrumbs={breadcrumbs || []}
      translation={translation}
      {...selectorProps}
    />;
  }

  const onOpen = () => {

    if(!isToggledOn) {
      handleOnToggle(false);
      return;
    }
  };

  return <FullscreenCodeSelector
    onOpen={onOpen}
    onClose={selectorProps.onClose}
    fetching={fetching}
    options={options}
    textGoBack={translation.back}
    routeHandler={router.replace}
    showToggleButton={bookmarksEnabled}
    onToggle={handleOnToggle}
    isToggledOn={isToggledOn}
    itemBookmark={renderItemBookmark}
    subtitleToggledOff={textNoCodesAdded}
    subtitleToggledOn={textNoBookmarksAdded}
    textSwitchTooltip={textToggleTooltip}
    validAuthState={validAuthState}
    title={bookmarksEnabled && overrideTitle}
    selectedFolder={selectedFolder}
    textCaseDraftTitle={caseDraftInfoPanelTitle}
    textCaseDraftInfoPanelContinue={caseDraftInfoPanelContinue}
    textCaseDraftInfoPanelExpires={caseDraftInfoPanelExpires}
    textCaseDraftInfoPanelViewAll={caseDraftInfoPanelViewAll}
    showCaseDraftInfoPanel={isCaseTypeSelector && draftsInfo?.count > 0}
    {...selectorProps}
  />;
};

CodeSelector.statePath = STATE.codeSelector;

export const selectors = {
  breadcrumbs: (_: CodetableAdministrationState, props: DefaultContainerProps) => getBreadcrumbs(props.params)
};

const container = containerFactory(CodeSelector, actionCreators, selectors, codeSelectorTranslations);

export { container as CodeSelector };
